import { useState, useEffect } from "react";
import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { useRoleNavigate } from "@/hooks/useRoleNavigate";
import { RoutesLink } from "@/utility/RouteLinks";
import { Plus, PencilIcon, Trash2, Eye, Download } from "lucide-react";
import CommonTable, { Action, TableColumn } from "@/components/common/CommonTable";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { IDiscount, IDiscountListResponse } from "@/interfaces/Discount/discount.interface";
import { useFilter } from "@/hooks/useFilter";
import { TableFilters } from "@/components/common/TableFilters";
import { IFilter, IPageInfo } from "@/interfaces/common.interface";
import { GetFilterUrl, showToastNotification, formatRand, formatStatus, downloadExcel } from "@/utility/helper";
import Pagination from "@/components/common/Pagination";
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Switch } from "@/components/ui/switch";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";
import { RootState, useAppSelector } from "@/redux/store";

export const Discount = () => {
    const navigate = useRoleNavigate();
    const { canCreate, canEdit, canDelete, canView, canExport } = usePermissions();
    const user = useAppSelector((state: RootState) => state?.user?.user);
    const [discounts, setDiscounts] = useState<IDiscount[]>([]);
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [discountToDelete, setDiscountToDelete] = useState<string | null>(null);

    const { filters, debouncedFilters, updateFilter, handlePageChange } = useFilter<IFilter>({
        search: '',
        page: 1,
        limit: 10,
        startDate: '',
        endDate: '',
    });

    const [loadingExport, setLoadingExport] = useState<boolean>(false);

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate,
            sort: filters.sort
        });
        const url = `${API_URL + API_ENDPOINT.discounts}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'discounts_export.xlsx');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };

    const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0,
    });

    const fetchDiscounts = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<IDiscountListResponse>(
                `${API_URL + API_ENDPOINT.discounts}?${queryParams}`,
                { method: 'GET' }
            );
            setDiscounts(response?.data?.discounts || []);
            setPageMetadata(response?.data?.pageInfo || {
                limit: 10,
                page: 1,
                total: 0,
                totalPages: 0,
            });
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDiscounts();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search, debouncedFilters.startDate, debouncedFilters.endDate]);

    const handleStatusToggle = async (id: string, currentStatus: string | null) => {
        setUpdatingId(id);
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            const response = await callApi<any>(`${API_URL}${API_ENDPOINT.discounts}/${id}`, {
                method: 'PATCH',
                data: { status: newStatus }
            });
            showToastNotification("success", response?.message || "Discount status updated successfully");

            // Optimistic Update
            setDiscounts(prev => prev.map(d => d.id === id ? { ...d, status: newStatus } : d));
        } catch (error) {
            ThrowError(error);
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDeleteDiscount = async () => {
        if (!discountToDelete) return;

        try {
            const response = await callApi<any>(`${API_URL}${API_ENDPOINT.discounts}/${discountToDelete}`, {
                method: 'DELETE'
            });
            showToastNotification("success", response?.message || "Discount deleted successfully");

            setDiscounts(prev => prev.filter(d => d.id !== discountToDelete));
        } catch (error) {
            ThrowError(error);
        } finally {
            setShowDeleteConfirm(false);
            setDiscountToDelete(null);
        }
    };

    const columns: TableColumn<IDiscount>[] = [
        {
            key: "name",
            header: "Discount Label",
            render: (item) => <span className="font-medium text-foreground">{item.name}</span>
        },
        {
            key: "promoTag",
            header: "Campaign Type",
            render: (item) => (
                <span className="font-medium text-muted-foreground capitalize">
                    {formatStatus(item.promoTag) || '---'}
                </span>
            )
        },
        {
            key: "value",
            header: "Value",
            render: (item) => (
                <div className="font-semibold text-primary">
                    {item.discountType === 'percentage' ? `${item.value}%` : formatRand(Number(item.value))}
                </div>
            )
        },
        {
            key: "targetType",
            header: "Applied to",
            render: (item) => (
                <div className="flex flex-col">
                    <span className="text-sm font-medium capitalize">{item.targetType}</span>
                    <span className="text-[10px] text-muted-foreground line-clamp-1">
                        {item.targetType === 'category' && item.category?.name || ''}
                    </span>
                </div>
            )
        },
        {
            key: "validity",
            header: "Validity",
            render: (item) => (
                <div className="flex flex-col text-[11px]">
                    <span className="text-foreground">Start: {item.startDate ? new Date(item.startDate).toLocaleDateString() : 'N/A'}</span>
                    <span className="text-muted-foreground">End: {item.endDate ? new Date(item.endDate).toLocaleDateString() : 'N/A'}</span>
                </div>
            )
        },
        {
            key: "status",
            header: "Status",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <Switch
                        checked={item.status === 'active'}
                        onCheckedChange={() => handleStatusToggle(item.id, item.status)}
                        disabled={updatingId === item.id}
                        aria-label="Toggle discount status"
                    />
                    <StatusBadge status={item.status || 'inactive'} />
                </div>
            )
        }
    ];

    const actions = (records: IDiscount): Action<IDiscount>[] => [
        ...(canView(RESOURCES.DISCOUNT) ? [{
            text: "View",
            icon: <Eye className="w-4 h-4 cursor-pointer text-muted-foreground" />,
            onClick: (record: IDiscount) => {
                navigate(RoutesLink.ViewDiscount.replace(':id', record.id));
            }
        }] : []),
        ...((canEdit(RESOURCES.DISCOUNT) && user?.id === records.userId) ? [{
            text: "Edit",
            icon: <PencilIcon className="w-4 h-4 cursor-pointer" />,
            onClick: (record: IDiscount) => {
                navigate(RoutesLink.EditDiscount.replace(':id', record.id));
            }
        }] : []),
        ...((canDelete(RESOURCES.DISCOUNT) && user?.id === records.userId) ? [{
            text: "Delete",
            icon: <Trash2 className="w-4 h-4 text-destructive cursor-pointer" />,
            onClick: (record: IDiscount) => {
                setDiscountToDelete(record.id);
                setShowDeleteConfirm(true);
            }
        }] : []),
    ];

    return (
        <Layout>
            <MainHeading
                title="Discounts"
                description="Manage your product campaigns and active offers"
                {...(canCreate(RESOURCES.DISCOUNT) && {
                    actionLabel: "Create Discount",
                    actionIcon: <Plus className="w-4 h-4" />,
                    onAction: () => navigate(RoutesLink.AddDiscount),
                })}
            />

            <div className="mt-6 flex flex-col gap-4">
                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search campaigns by label..."
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        {canExport(RESOURCES.DISCOUNT) && <Button
                            variant="outline"
                            onClick={() => handleExport()}
                            className="h-10 px-4 flex items-center gap-2 rounded-xl"
                            leftIcon={<Download className="w-4 h-4" />}
                            isLoading={loadingExport}
                        >
                            Export
                        </Button>}
                    </div>
                </TableFilters>
                <CommonTable
                    columns={columns}
                    data={discounts}
                    loading={loading}
                    emptyMessage="No discount campaigns found."
                    actions={actions}
                />

                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageMetadata.totalPages}
                    totalItems={pageMetadata.total}
                    itemsPerPage={pageMetadata.limit}
                    onPageChange={handlePageChange}
                />
            </div>

            {/* ── Delete Confirmation Modal ── */}
            <ConfirmationModal
                open={showDeleteConfirm}
                onOpenChange={(open) => !open && setShowDeleteConfirm(false)}
                onConfirm={handleDeleteDiscount}
                title="Confirm Deletion"
                message="Are you sure you want to delete this discount? This action cannot be undone."
                confirmText="Yes, Delete"
                cancelText="Cancel"
                size="lg"
            />
        </Layout>
    );
};
